import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'risks'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	bySite: {} as Record<string, any[]>,
	comparisonByEvaluation: {} as Record<string, any[]>,
	isLoading: false,
	error: null as string | null,
}

export const fetchSiteRisks = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (
		{ evalId, siteId }: { evalId: string; siteId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/sites/${siteId}/risks`,
				prepConfig(getState),
			)
			return { siteId, risks: data }
		} catch {
			return rejectWithValue('Failed to load risks')
		}
	},
)

export const createRisk = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (
		{ evalId, siteId, payload }: { evalId: string; siteId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/sites/${siteId}/risks`,
				payload,
				prepConfig(getState),
			)
			return { siteId, risk: data }
		} catch {
			return rejectWithValue('Failed to create risk')
		}
	},
)

export const updateRisk = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (
		{
			evalId,
			siteId,
			riskId,
			payload,
		}: { evalId: string; siteId: string; riskId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/sites/${siteId}/risks/${riskId}`,
				payload,
				prepConfig(getState),
			)
			return { siteId, risk: data }
		} catch {
			return rejectWithValue('Failed to update risk')
		}
	},
)

export const deleteRisk = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (
		{ evalId, siteId, riskId }: { evalId: string; siteId: string; riskId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			await axios.delete(
				`/api/evaluations/${evalId}/sites/${siteId}/risks/${riskId}`,
				prepConfig(getState),
			)
			return { siteId, riskId }
		} catch {
			return rejectWithValue('Failed to delete risk')
		}
	},
)

export const fetchRisksComparison = createAsyncThunk(
	`${SLICE_NAME}/fetchCompare`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/risks/compare`,
				prepConfig(getState),
			)
			return { evalId, comparison: data }
		} catch {
			return rejectWithValue('Failed to load risks comparison')
		}
	},
)

// ============================================================================
// Apply a risk template across the evaluation's sites.
//
// Mirrors POST /api/evaluations/:evalId/apply-risk-template. The shared
// ApplyTemplateModalCN calls this URL directly via axios — this thunk is a
// Redux-shaped affordance for callers that want dispatch visibility.
// Callers should re-fetch the comparison after apply to refresh both
// panes.
// ============================================================================
export const applyRiskTemplate = createAsyncThunk(
	`${SLICE_NAME}/applyTemplate`,
	async (
		payload: {
			evalId: string
			templateId: string
			overwrite?: boolean
			applyTo?: 'all' | string
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const { evalId, templateId, overwrite, applyTo } = payload
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/apply-risk-template`,
				{
					templateId,
					overwrite: !!overwrite,
					applyTo: applyTo || 'all',
				},
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to apply risk template',
			)
		}
	},
)

const riskSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSiteRisks.fulfilled, (state, action) => {
				const { siteId, risks } = action.payload
				state.bySite[siteId] = risks
			})
			.addCase(createRisk.fulfilled, (state, action) => {
				const { siteId, risk } = action.payload
				if (!state.bySite[siteId]) state.bySite[siteId] = []
				state.bySite[siteId].unshift(risk)
			})
			.addCase(updateRisk.fulfilled, (state, action) => {
				const { siteId, risk } = action.payload
				const list = state.bySite[siteId] || []
				const idx = list.findIndex((r) => r._id === risk._id)
				if (idx >= 0) list[idx] = risk
			})
			.addCase(deleteRisk.fulfilled, (state, action) => {
				const { siteId, riskId } = action.payload
				if (state.bySite[siteId]) {
					state.bySite[siteId] = state.bySite[siteId].filter((r) => r._id !== riskId)
				}
			})
			.addCase(fetchRisksComparison.fulfilled, (state, action) => {
				const { evalId, comparison } = action.payload
				state.comparisonByEvaluation[evalId] = comparison
			})
	},
})

export default riskSlice
